// The output that shows on a transaction processing success/failure
import * as React from 'react'
import { useContext, useEffect, useState } from 'react'

import { Fieldset, Legend, PrimaryButton } from '@paytrace/pt-skel/Component'
import { AppCtx } from '@paytrace/pt-skel/Context'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import Event from '../Event'

import { getLegacyDomain } from '@paytrace/pt-skel/Helper'
import { viewReceipt } from '../Helper/LegacyURLs'

interface Props {
  transaction_id: number
  response_code: number
  transaction_identifier: string
  response_message: string
  approval_code: string
  approval_message: string
  requested_amount: number
  surcharge_amount?: number
  total_amount: number,
  include_surcharge: boolean
  emails: string[]
  merchant: number
  customer_id: number
  reprocessFn: any
}

const EMPTY_CUSTOMER_ID = -1

const APPROVED_RESPONSE_CODES = {
  APPROVED: 101,
  REFUNDED: 106,
  CAPTURED: 112,
  FORCED: 115,
  VOIDED: 109,
}

function getViewDetailHref (id: string) {
  return `${getLegacyDomain()}/view.pay?b=${id}`
}

export function viewInvoice (merchantId: string, id: string) {
  window.open(`${getLegacyDomain()}/invoice.pay?tranxid=${id}&id=${merchantId}`, '_blank')
}

export function viewDetails (id: string) {
  window.open(getViewDetailHref(id), '_blank')
}

function renderOrderId (id: string) {
  const href = getViewDetailHref(id)

  if (id === 'pending') {
    return (
      <div>(<span
        title='Order ID is being derived, you do not need to wait here
                     while it processes, but will see it appear as it does.'
        className='uid-tranx-app-pending animate-flicker pending'>pending</span>)</div>
    )
  }

  return (
    <a href={href}
      rel='noreferrer'
      className='uid-tranx-app-view-detail'
      target='_blank'>
      {id}
    </a>
  )
}

export function saveAndOrEmailButton (orderId: string, emails: string[], doSaveCard: boolean, customerId: Number, merchantID: Number) {
  const doSendEmail = emails.length > 0

  const sendEmailAction = () => {
    PubSub.pub(Event.SEND_SINGLE_EMAIL_RECEIPT_REQ, {
      to: emails,
      transaction_id: orderId,
      merchant_id: merchantID,
    })
  }

  const saveCardAction = () => {
    location.href = (customerId === EMPTY_CUSTOMER_ID)
      ? `${getLegacyDomain()}/mcust.pay?new=Y&tranxid=${orderId}`
      : `${getLegacyDomain()}/mcust.pay?tranxid=${orderId}&cid=${String(customerId)}`
  }

  let actions: { (): void; }[]
  let buttonLabel: String = ''

  if (doSendEmail && doSaveCard) {
    actions = [sendEmailAction, saveCardAction]
    buttonLabel = 'Save and Email'
  } else if (doSendEmail) {
    actions = [sendEmailAction]
    buttonLabel = 'Email'
  } else if (doSaveCard) {
    actions = [saveCardAction]
    buttonLabel = 'Save Card'
  }

  const doActions = () => { actions.forEach(a => a()) }

  const button = (
    <PrimaryButton onClick={doActions} className='uid-tranx-app-save-or-email'>
    <b>&#10003;</b> {buttonLabel}
    </PrimaryButton>
  )

  return (doSendEmail || doSaveCard) && button
}

export function ResponseFeedback (props: Props) {
  const context = useContext(AppCtx)
  const [availableEmails, setAvailableEmails] = useState<string[]>([...props.emails])
  const [_isLoading, setIsLoading] = useState(false)
  const [emails, setEmails] = useState<string[]>([])
  const orderId = String(props.transaction_id)
  const [saveCard, setSaveCard] = useState<boolean>(false)

  useEffect(() => {
    const payload = {
      merchant_id: props.merchant,
      email_type: '6',
    }
    setIsLoading(true)
    PubSub.pub(Event.GET_CONFIGURED_EMAIL_LIST_REQUEST, payload)
  }, [props.merchant])

  useEffect(() => {
    PubSub.sub(
      self,
      Event.GET_CONFIGURED_EMAIL_LIST_RESPONSE,
      (response) => {
        const emails = [...props.emails]
        response.forEach((data: { ['email']: string }) => {
          emails.push(data.email)
        })
        setAvailableEmails([...emails])
        setIsLoading(false)
      }
    )
  })

  const emailClickHandler = (whom: string) => (e: any) => {
    // I must be missing the type, tsc says checked doesn't exist, but it does...
    if (e.target.checked) {
      setEmails([...emails, whom])
    } else {
      setEmails(emails.filter(email => email !== whom))
    }
  }

  const clickableEmail = (whom: string) => {
    return (
      <label style={{ cursor: 'pointer', display: 'block' }}>
        <input
          type='checkbox'
          className='uid-tranx-app-email'
          onClick={emailClickHandler(whom)} /> {whom}
      </label>
    )
  }

  const buildSuccessfullEmailMsg = (emails: string[]) => {
    let msg = ''
    emails.forEach((email: string) => {
      msg = msg + `The receipt was successfully emailed to ${email}.\n`
    })
    return msg
  }

  useEffect(() => {
    PubSub.sub(self, Event.SEND_SINGLE_EMAIL_RECEIPT_RES,
      (emailResponse) => {
        if (emailResponse.error_code === 0) {
          alert(buildSuccessfullEmailMsg(emailResponse.to.split(',')))
        } else {
          alert('There was a problem sending the email')
        }
      })
  }, [])

  function renderAmounts (): React.ReactElement {
    if (props.include_surcharge) {
      return (
          <>
              <tr>
                  <td style={{
                    paddingRight: '20px',
                    textAlign: 'right',
                  }}>
                      <strong>Total Amount</strong>
                  </td>
                  <td className="uid-tranx-app-total-amount">
                      ${Number(props.total_amount).toFixed(2)}
                  </td>
              </tr>
              <tr>
                  <td style={{
                    paddingRight: '20px',
                    textAlign: 'right',
                  }}>
                      <strong>Transaction Amount</strong>
                  </td>
                  <td className="uid-tranx-app-transaction-amount">
                      ${Number(props.requested_amount).toFixed(2)}
                  </td>
              </tr>
              <tr>
                  <td style={{
                    paddingRight: '20px',
                    textAlign: 'right',
                  }}>
                      <strong>Surcharge Amount</strong>
                  </td>
                  <td className="uid-tranx-app-surcharge-amount">
                      ${Number(props.surcharge_amount).toFixed(2)}
                  </td>
              </tr>
          </>
      )
    } else {
      return (
        <>
          <tr>
            <td style={{
              paddingRight: '20px',
              textAlign: 'right',
            }}>
              <strong>Amount</strong>
            </td>
            <td className="uid-tranx-app-transaction-amount">
              ${Number(props.requested_amount).toFixed(2)}
            </td>
          </tr>
        </>

      )
    }
  }

  // TODO: Add the Email Receipts To functionality/call
  // TODO: Add the Save Card "Create a customer from this transaction" feature
  let responseBanner = <div className='uid-tranx-app-error center error giant-text'>Not Approved</div>

  if (Object.values(APPROVED_RESPONSE_CODES).includes(props.response_code)) {
    responseBanner = <div className='uid-tranx-app-success center success giant-text'>Approved</div>
  }

  return (
      <Fieldset>
        <Legend title='Transaction Details'>
        <div className='float-right'>
          <button
            className='uid-tranx-app-reprocess-card'
            onClick={props.reprocessFn}
          >Reprocess Card</button>
          <button
            className='uid-tranx-app-details'
            onClick={() => viewDetails(orderId)}
          >Details</button>
          <button
            className='uid-tranx-app-receipt'
            onClick={() => viewReceipt(context.merchant, orderId)}
          >Receipt</button>
          <button
            className='uid-tranx-app-invoice'
            onClick={() => viewInvoice(context.merchant, orderId)}
          >Invoice</button>
        </div>
      </Legend>
      <div className='uid-tranx-app-clear clear'></div>
        {responseBanner}
      <br />
      <table style={{ margin: 'auto' }}>

        {renderAmounts()}

        <tr>
          <td style={{ paddingRight: '20px', textAlign: 'right' }}>
            <strong>Approval&nbsp;Code</strong>
          </td>
          <td className='uid-tranx-app-approval-code'>{props.approval_code}</td>
        </tr>

        <tr>
          <td style={{ paddingRight: '20px', textAlign: 'right' }}>
            <strong>Response</strong>
          </td>
          <td className='uid-tranx-app-response'>
            {props.approval_message}{(Object.values(APPROVED_RESPONSE_CODES).includes(props.response_code)) && ' - Approved and completed'}
          </td>
        </tr>

        <tr>
          <td style={{ paddingRight: '20px', textAlign: 'right' }}>
            <strong>Order&nbsp;ID</strong>
          </td>
          <td className='uid-tranx-app-order-id'>
            {renderOrderId(orderId)}
          </td>
        </tr>

        <tr><td>&nbsp;</td><td>&nbsp;</td></tr>

        <tr>
          <td style={{ paddingRight: '20px', textAlign: 'right' }}>
            Email Receipts To
          </td>
          <td className='uid-tranx-app-available-emails'>
            {availableEmails.map(clickableEmail)}
          </td>
        </tr>

        <tr>
          <td style={{ paddingRight: '20px', textAlign: 'right' }}>
            Save Card
          </td>
          <td>
          <label style={{ cursor: 'pointer', display: 'block' }}>
            <input
              className='uid-tranx-create-or-update-customer'
              type='checkbox'
              onChange={(e) => setSaveCard(e.target.checked)} />
              {(props.customer_id === EMPTY_CUSTOMER_ID)
                ? 'Create a customer from this transaction'
                : 'Update customer\'s billing information'}
          </label>
          </td>
        </tr>

        <tr>
          <td style={{ paddingRight: '20px', textAlign: 'right' }}>
            &nbsp;
          </td>
          <td className='uid-tranx-app-save-or-email-button-container'>
            {saveAndOrEmailButton(orderId, emails, saveCard, props.customer_id, props.merchant)}
          </td>
        </tr>

      </table>
    </Fieldset>
  )
}
